// §9.4 — an icon an *app* supplied is data, never markup the shell executes.
//
// `HangarAPI.window.setIcon(svg)` takes a string from an app on another origin, and every skin
// renders a window icon with `dangerouslySetInnerHTML`. Injecting that string directly would be
// script execution on the shell's origin — which is the one thing §9.4's trust model is built to
// prevent, since the origin *is* the boundary and the iframe sandbox deliberately is not.
//
// So the string never becomes shell DOM. It becomes the source of an `<img>`, and an SVG loaded
// through `<img>` is in the browser's secure static mode: no scripts, no event handlers, no
// external fetches, enforced by the browser rather than by a regex of mine. A hostile `<svg
// onload=…>` renders as a picture of nothing.

/** A hostile app should not be able to make the session blob enormous. */
export const MAX_APP_ICON_BYTES = 64 * 1024;

/**
 * Wrap an app-supplied SVG for safe rendering, or return `null` when it is not usable — in which
 * case the caller keeps whatever icon the window already had (D-122).
 */
export function appSuppliedIconMarkup(svg: string, size = 32): string | null {
  const trimmed = svg.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_APP_ICON_BYTES) return null;
  // It has to at least claim to be an SVG document. Anything else is not an icon.
  if (!/^<svg[\s>]/i.test(trimmed)) return null;

  // `encodeURIComponent` escapes `"`, `<` and `&`, so the result cannot break out of the attribute
  // it is about to sit in — the encoding is the escaping.
  const src = `data:image/svg+xml,${encodeURIComponent(trimmed)}`;
  return `<img src="${src}" width="${size}" height="${size}" alt="" decoding="async" />`;
}
